import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useCampaign,
  useRefreshCache,
  useCampaignMetrics,
  useUpdateExecutionStatus,
} from '@/hooks/useCampaigns';
import { useDashboardConfig, useSaveDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAISuggestWidgets } from '@/hooks/useAISuggestWidgets';
import { useMetricConfig } from '@/hooks/useMetricConfig';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { WidgetToolbar } from '@/components/dashboard/WidgetToolbar';
import { MetricConfigDrawer } from '@/components/MetricConfigDrawer';
import { StatusSelect } from '@/components/StatusSelect';
import { OptimizationsSection } from '@/components/optimizations/OptimizationsSection';
import { formatDate, cn } from '@/lib/utils';
import type { LayoutItem, WidgetConfig, MetricDefinition } from '@monitoring-panel/types';
import { FIXED_METRICS, OPTIONAL_METRICS } from '@monitoring-panel/types';

export function CampaignDetailView() {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const [isMetricConfigOpen, setIsMetricConfigOpen] = useState(false);

  const { data: campaignData, isLoading: campaignLoading, error: campaignError } = useCampaign(executionId || '');
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboardConfig(executionId || '');
  const { data: metricConfigData } = useMetricConfig(executionId || '');
  const { data: metricsData } = useCampaignMetrics(executionId || '');
  const saveDashboard = useSaveDashboardConfig(executionId || '');
  const refreshCache = useRefreshCache();
  const updateStatus = useUpdateExecutionStatus(executionId || '');
  const aiSuggest = useAISuggestWidgets();

  const campaign = campaignData?.campaign;
  const dashboardConfig = dashboardData?.config;
  const metricConfig = metricConfigData?.config;

  // Build list of all available metrics (fixed + enabled optional + custom)
  const availableMetrics: MetricDefinition[] = [
    ...FIXED_METRICS,
    ...OPTIONAL_METRICS.filter((m) => metricConfig?.enabledOptionalMetrics?.includes(m.id)),
    ...(metricConfig?.customMetrics || []),
  ];

  const handleRefresh = () => {
    if (executionId) {
      refreshCache.mutate(executionId);
    }
  };

  const handleLayoutChange = (newLayout: LayoutItem[]) => {
    if (!executionId) return;
    saveDashboard.mutate({
      layout: newLayout,
      widgets: dashboardConfig?.widgets || [],
    });
  };

  const handleAddWidget = (widget: WidgetConfig) => {
    if (!executionId) return;
    const currentWidgets = dashboardConfig?.widgets || [];
    const currentLayout = dashboardConfig?.layout || [];

    // Find the next available Y position
    const maxY = currentLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

    const newLayoutItem: LayoutItem = {
      i: widget.id,
      x: 0,
      y: maxY,
      w: widget.type === 'chart' ? 6 : 6,
      h: widget.type === 'chart' ? 4 : 3,
      minW: widget.type === 'chart' ? 4 : 3,
      minH: widget.type === 'chart' ? 3 : 2,
    };

    saveDashboard.mutate({
      layout: [...currentLayout, newLayoutItem],
      widgets: [...currentWidgets, widget],
    });
  };

  const handleUpdateWidget = (widgetId: string, updates: Partial<WidgetConfig>) => {
    if (!executionId || !dashboardConfig) return;
    const updatedWidgets = dashboardConfig.widgets.map((w) =>
      w.id === widgetId ? { ...w, ...updates } : w
    );
    saveDashboard.mutate({
      layout: dashboardConfig.layout,
      widgets: updatedWidgets as WidgetConfig[],
    });
  };

  const handleDeleteWidget = (widgetId: string) => {
    if (!executionId || !dashboardConfig) return;
    saveDashboard.mutate({
      layout: dashboardConfig.layout.filter((l) => l.i !== widgetId),
      widgets: dashboardConfig.widgets.filter((w) => w.id !== widgetId),
    });
  };

  // Aggregate data for custom metric preview
  const aggregatedData = campaign?.data
    ? aggregateData(campaign.data)
    : {};

  function aggregateData(rows: Array<Record<string, unknown>>): Record<string, number> {
    const result: Record<string, number> = {};
    for (const row of rows) {
      for (const [key, value] of Object.entries(row)) {
        if (typeof value === 'number') {
          result[key] = (result[key] || 0) + value;
        }
      }
    }
    return result;
  }

  const handleAISuggest = async () => {
    if (!campaign || !executionId) return;
    try {
      const result = await aiSuggest.mutateAsync({
        metadata: campaign.metadata,
        availableColumns: campaign.availableColumns,
        availableMetrics,
        campaignData: campaign.data,
      });
      saveDashboard.mutate({ layout: result.layout, widgets: result.widgets });
    } catch {
      toast.error('AI suggestion failed. Please try again.');
    }
  };

  if (campaignLoading || dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-adv-accent" size={32} />
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="adv-alert">
        {campaignError ? `Error: ${campaignError.message}` : 'Campaign not found'}
      </div>
    );
  }

  const { metadata } = campaign;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/executions')}
            className="adv-btn adv-btn--ghost adv-btn--icon"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="adv-page-title">
              <span className="adv-num">[{metadata.executionId}]</span> {metadata.campaignName}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-adv-body-m text-adv-secondary">
              <StatusSelect
                status={metadata.status}
                onChange={(status) => updateStatus.mutate(status)}
                isPending={updateStatus.isPending}
              />
              <span className="adv-num">
                {formatDate(metadata.startDate)} — {formatDate(metadata.endDate)}
              </span>
              <span>{metadata.specialist}</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshCache.isPending}
          className="adv-btn adv-btn--primary"
        >
          <RefreshCw size={16} className={cn(refreshCache.isPending && 'animate-spin')} />
          Refresh from BigQuery
        </button>
      </div>

      {/* Widget Toolbar */}
      <WidgetToolbar
        availableColumns={campaign.availableColumns}
        campaignData={campaign.data}
        availableMetrics={availableMetrics}
        onAddWidget={handleAddWidget}
        onOpenMetricConfig={() => setIsMetricConfigOpen(true)}
      />

      {/* Dashboard Grid */}
      <DashboardGrid
        layout={dashboardConfig?.layout || []}
        widgets={dashboardConfig?.widgets || []}
        campaignData={campaign.data}
        availableColumns={campaign.availableColumns}
        availableMetrics={availableMetrics}
        onLayoutChange={handleLayoutChange}
        onUpdateWidget={handleUpdateWidget}
        onDeleteWidget={handleDeleteWidget}
      />

      {/* Optimization reports of the delivery-report skill */}
      {executionId && <OptimizationsSection executionId={executionId} />}

      {/* Empty State */}
      {(!dashboardConfig?.widgets || dashboardConfig.widgets.length === 0) && (
        <div className="adv-card adv-card--featured adv-state">
          <p className="adv-state__desc text-adv-primary mb-2">
            No widgets yet. Use the buttons above to add tables or charts.
          </p>
          <p className="text-adv-body-s text-adv-secondary mb-6">
            Drag and drop widgets to rearrange them. Your layout will be saved automatically.
          </p>
          <button
            onClick={handleAISuggest}
            disabled={aiSuggest.isPending || saveDashboard.isPending}
            className="adv-btn adv-btn--primary"
          >
            {aiSuggest.isPending || saveDashboard.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <img src="/vera.gif" alt="Vera" className="w-5 h-5 object-cover rounded-adv-sm" />
            )}
            {aiSuggest.isPending ? 'Thinking…' : saveDashboard.isPending ? 'Saving…' : 'Suggest with AI'}
          </button>
        </div>
      )}

      {/* Metric Config Drawer */}
      {executionId && (
        <MetricConfigDrawer
          isOpen={isMetricConfigOpen}
          onClose={() => setIsMetricConfigOpen(false)}
          executionId={executionId}
          metrics={metricsData?.metrics || []}
          availableColumns={campaign?.availableColumns || []}
          sampleData={aggregatedData}
          metadata={campaign.metadata}
          availableMetrics={availableMetrics}
          campaignData={campaign.data}
        />
      )}
    </div>
  );
}
